import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LOGO_URL } from "@/lib/brand";
import CubeCanvas from "@/components/CubeCanvas";
import { ArrowLeft, Upload, Type, Image as ImageIcon, Rotate3d, Sparkles, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const GOLD = "#B2A475";
const GOLD_RGBA = (a) => `rgba(178,164,117,${a})`;

const COLORS = [
  { id: "oro", name: "Oro", hex: "#B2A475" },
  { id: "bianco", name: "Bianco", hex: "#F5F1E6" },
  { id: "nero", name: "Nero", hex: "#141414" },
  { id: "rosso", name: "Rosso", hex: "#7c2020" },
  { id: "blu", name: "Blu notte", hex: "#1b3a5b" },
  { id: "verde", name: "Verde bosco", hex: "#24422f" },
  { id: "bordeaux", name: "Bordeaux", hex: "#4a1f2b" },
  { id: "tortora", name: "Tortora", hex: "#8a8175" },
];

const SCENES = [
  { id: "scuro", name: "Scuro elegante", css: "radial-gradient(circle at 50% 35%, #3a3a40 0%, #17171a 78%)" },
  { id: "tavolo", name: "Tavolo ristorante", img: "https://images.unsplash.com/photo-1469234496837-d0101f54be3e?q=80&w=1200" },
  { id: "bar", name: "Bancone bar", img: "https://images.unsplash.com/photo-1578911489158-334e5cd2a051?q=80&w=1200" },
];

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: GOLD }}>{title}</p>
    {children}
  </div>
);

const CubeConfigurator = () => {
  const [color, setColor] = useState(COLORS[0].hex);
  const [finish, setFinish] = useState("lucido"); // lucido | opaco
  const [material, setMaterial] = useState("acrilico"); // acrilico | legno
  const [sideMode, setSideMode] = useState("testo"); // testo | logo
  const [sideText, setSideText] = useState("");
  const [sideLogo, setSideLogo] = useState(null);
  const [scene, setScene] = useState(SCENES[0]);
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });

  const colorName = COLORS.find((c) => c.hex === color)?.name || "personalizzato";
  const activeLogo = sideMode === "logo" ? sideLogo : null;
  const activeText = sideMode === "testo" ? sideText : "";

  const onLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setSideLogo(reader.result); setSideMode("logo"); };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const summary = () => {
    const parts = [
      `Materiale: ${material === "legno" ? "Legno" : "Acrilico"}`,
      material === "legno" ? null : `Finitura: ${finish === "lucido" ? "Lucida" : "Opaca"}`,
      material === "legno" ? null : `Colore: ${colorName}`,
      sideMode === "logo" && sideLogo ? "Facce laterali: logo caricato dal cliente" : (sideText ? `Facce laterali: testo "${sideText}"` : "Facce laterali: nessuna personalizzazione"),
    ].filter(Boolean);
    return parts.join(" · ");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: `Richiesta configurazione Cube personalizzato.\n\n${summary()}`,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success("Richiesta inviata! Ti ricontatteremo con il listino.");
      setShowForm(false); setForm({ name: "", email: "" });
    } catch {
      toast.error("Invio non riuscito. Scrivici a info@thecubedesign.it");
    } finally { setSending(false); }
  };

  const sceneStyle = scene.img
    ? { backgroundImage: `linear-gradient(rgba(11,11,12,0.55),rgba(11,11,12,0.75)), url(${scene.img})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: scene.css };

  return (
    <div className="min-h-screen" style={{ color: "#F5F1E6", fontFamily: "'Manrope', sans-serif", backgroundColor: "#0B0B0C" }}>
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b" style={{ backgroundColor: "rgba(11,11,12,0.72)", borderColor: GOLD_RGBA(0.18) }}>
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="config-back-home">
            <img src={LOGO_URL} alt="logo" className="w-9 h-9 object-contain" />
            <span className="tracking-widest uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem" }}>The Cube Design</span>
          </Link>
          <Link to="/#prodotti" className="text-sm flex items-center gap-1.5" style={{ color: "rgba(245,241,230,0.6)" }}><ArrowLeft className="w-4 h-4" /> Prodotti</Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-24 pb-16">
        <div className="mb-8">
          <span className="text-xs uppercase tracking-[0.35em]" style={{ color: GOLD }}>Configuratore</span>
          <h1 className="mt-3 font-medium text-4xl md:text-5xl gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Personalizza il tuo cubo</h1>
          <p className="mt-3 max-w-2xl leading-relaxed" style={{ color: "rgba(245,241,230,0.6)" }}>Trascina il cubo per ruotarlo e osservarlo da ogni lato. Colore, finitura, materiale e facce laterali si aggiornano in tempo reale. La faccia superiore con placca, QR e NFC è un elemento fisso del prodotto.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Preview */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden self-start" style={{ border: `1px solid ${GOLD_RGBA(0.25)}`, ...sceneStyle }}>
            <CubeCanvas className="w-full h-[440px] md:h-[560px]" color={color} finish={finish} material={material} sideText={activeText} sideLogo={activeLogo} />
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: "rgba(11,11,12,0.6)", color: "rgba(245,241,230,0.7)", border: `1px solid ${GOLD_RGBA(0.25)}` }}>
              <Rotate3d className="w-3.5 h-3.5" /> Trascina per ruotare
            </div>
          </motion.div>

          {/* Controls */}
          <div className="space-y-7">
            <Section title="Materiale e finitura">
              <div className="flex flex-wrap gap-2 mb-3">
                {[["acrilico", "Acrilico"], ["legno", "Legno"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setMaterial(k)} data-testid={`material-${k}`}
                    className="rounded-full px-5 py-2 text-sm font-semibold transition-all border"
                    style={material === k ? { backgroundColor: GOLD, color: "#0B0B0C", borderColor: GOLD } : { borderColor: GOLD_RGBA(0.35), color: "#F5F1E6" }}>{lbl}</button>
                ))}
              </div>
              <div className={`flex flex-wrap gap-2 transition-opacity ${material === "legno" ? "opacity-40 pointer-events-none" : ""}`}>
                {[["lucido", "Lucido"], ["opaco", "Opaco"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setFinish(k)} data-testid={`finish-${k}`}
                    className="rounded-full px-5 py-2 text-sm font-medium transition-all border"
                    style={finish === k ? { backgroundColor: GOLD_RGBA(0.18), color: GOLD, borderColor: GOLD } : { borderColor: GOLD_RGBA(0.25), color: "rgba(245,241,230,0.7)" }}>{lbl}</button>
                ))}
              </div>
            </Section>

            <Section title="Colore">
              <div className={`flex flex-wrap gap-3 transition-opacity ${material === "legno" ? "opacity-40 pointer-events-none" : ""}`}>
                {COLORS.map((c) => (
                  <button key={c.id} onClick={() => setColor(c.hex)} title={c.name} data-testid={`color-${c.id}`}
                    className="w-10 h-10 rounded-full transition-transform hover:scale-110 relative"
                    style={{ backgroundColor: c.hex, boxShadow: color === c.hex ? `0 0 0 2px #0B0B0C, 0 0 0 4px ${GOLD}` : "0 0 0 1px rgba(255,255,255,0.15)" }}>
                    {color === c.hex && <Check className="w-4 h-4 absolute inset-0 m-auto" style={{ color: c.hex === "#F5F1E6" || c.hex === "#B2A475" || c.hex === "#8a8175" ? "#0B0B0C" : "#fff" }} />}
                  </button>
                ))}
              </div>
              <p className="text-xs mt-2" style={{ color: "rgba(245,241,230,0.4)" }}>La base è sempre in PVC nero opaco. Con il legno il colore non è selezionabile.</p>
            </Section>

            <Section title="Facce laterali">
              <div className="inline-flex rounded-full p-1 mb-3" style={{ backgroundColor: "rgba(255,255,255,0.05)", border: `1px solid ${GOLD_RGBA(0.25)}` }}>
                {[["testo", "Testo / Numero", Type], ["logo", "Logo", ImageIcon]].map(([k, lbl, Icon]) => (
                  <button key={k} onClick={() => setSideMode(k)} data-testid={`side-mode-${k}`}
                    className="text-sm font-semibold rounded-full px-4 py-1.5 transition-all flex items-center gap-1.5"
                    style={sideMode === k ? { backgroundColor: GOLD, color: "#0B0B0C" } : { color: "rgba(245,241,230,0.6)" }}>
                    <Icon className="w-3.5 h-3.5" /> {lbl}
                  </button>
                ))}
              </div>
              {sideMode === "testo" ? (
                <input value={sideText} onChange={(e) => setSideText(e.target.value.slice(0, 14))} placeholder="Es. il nome del locale o un numero"
                  data-testid="side-text-input" className="w-full rounded-xl px-4 py-3 focus:outline-none" style={{ backgroundColor: "rgba(255,255,255,0.05)", border: `1px solid ${GOLD_RGBA(0.3)}`, color: "#F5F1E6" }} />
              ) : (
                <div className="flex items-center gap-3">
                  <label data-testid="side-logo-upload" className="cursor-pointer rounded-xl px-5 py-3 text-sm font-medium flex items-center gap-2 transition-colors hover:bg-white/5" style={{ border: `1px solid ${GOLD_RGBA(0.35)}`, color: "#F5F1E6" }}>
                    <Upload className="w-4 h-4" /> Carica logo
                    <input type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="side-logo-input" />
                  </label>
                  {sideLogo && (
                    <button onClick={() => setSideLogo(null)} data-testid="side-logo-clear" className="flex items-center gap-1.5 text-sm" style={{ color: "rgba(245,241,230,0.6)" }}>
                      <X className="w-4 h-4" /> Rimuovi
                    </button>
                  )}
                </div>
              )}
            </Section>

            <Section title="Ambientazione">
              <div className="flex flex-wrap gap-2">
                {SCENES.map((s) => (
                  <button key={s.id} onClick={() => setScene(s)} data-testid={`scene-${s.id}`}
                    className="rounded-full px-4 py-2 text-sm font-medium transition-all border"
                    style={scene.id === s.id ? { backgroundColor: GOLD_RGBA(0.18), color: GOLD, borderColor: GOLD } : { borderColor: GOLD_RGBA(0.25), color: "rgba(245,241,230,0.7)" }}>{s.name}</button>
                ))}
              </div>
            </Section>

            <div className="pt-2 border-t" style={{ borderColor: GOLD_RGBA(0.15) }}>
              <p className="text-sm mb-4" style={{ color: "rgba(245,241,230,0.55)" }}><span style={{ color: GOLD }}>La tua configurazione:</span> {summary()}</p>
              <button onClick={() => setShowForm((v) => !v)} data-testid="request-cube-btn"
                className="rounded-full font-semibold px-8 py-3.5 flex items-center gap-2 transition-all hover:-translate-y-0.5 hover:shadow-[0_0_45px_-6px_#B2A475]" style={{ backgroundColor: GOLD, color: "#0B0B0C" }}>
                <Sparkles className="w-4 h-4" /> Richiedi questo cubo
              </button>
            </div>

            {showForm && (
              <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} onSubmit={submit}
                className="rounded-2xl p-5 space-y-3" style={{ border: `1px solid ${GOLD_RGBA(0.25)}`, backgroundColor: "rgba(14,14,14,0.9)" }} data-testid="config-request-form">
                <p className="text-sm" style={{ color: "rgba(245,241,230,0.7)" }}>Ti inviamo il listino con questa configurazione. Nessun impegno.</p>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome e locale" data-testid="config-name" className="w-full rounded-xl px-4 py-3 focus:outline-none" style={{ backgroundColor: "rgba(255,255,255,0.05)", border: `1px solid ${GOLD_RGBA(0.3)}`, color: "#F5F1E6" }} />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="config-email" className="w-full rounded-xl px-4 py-3 focus:outline-none" style={{ backgroundColor: "rgba(255,255,255,0.05)", border: `1px solid ${GOLD_RGBA(0.3)}`, color: "#F5F1E6" }} />
                <button type="submit" disabled={sending} data-testid="config-send" className="w-full rounded-full font-semibold py-3 flex items-center justify-center gap-2" style={{ backgroundColor: GOLD, color: "#0B0B0C" }}>{sending && <Loader2 className="w-4 h-4 animate-spin" />} Invia richiesta</button>
              </motion.form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CubeConfigurator;
